// All DOM-overlay manipulation lives here. The 3D world never touches the DOM
// directly — it calls these methods. Elements are declared in public/index.html.

import type { DialogueChoice } from "../shared/types.js";

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`HUD element #${id} missing from index.html`);
  return e as T;
}

export class Hud {
  private crosshair = el("crosshair");
  private prompt = el("prompt");
  private objective = el("objective");
  private toast = el("toast");
  private vignette = el("vignette");
  private redflash = el("redflash");
  private watch = el("watch");
  private watchDigits = el("watch-digits");
  private watchLabel = el("watch-label");
  private dialogue = el("dialogue");
  private dialogueSay = el("dialogue-say");
  private dialogueChoices = el("dialogue-choices");
  private toastTimer = 0;

  // ── Reticle ──
  setCrosshair(active: boolean, hot = false) {
    this.crosshair.classList.toggle("active", active);
    this.crosshair.classList.toggle("hot", hot);
  }

  // ── Interaction prompt ──
  showPrompt(text: string) {
    this.prompt.innerHTML = `<span class="key">E</span>${text}`;
    this.prompt.classList.add("show");
  }
  hidePrompt() {
    this.prompt.classList.remove("show");
  }

  // ── Objective banner ──
  setObjective(html: string) {
    this.objective.innerHTML = html;
  }

  // ── Agent activity feed (spectator) ──
  private feed = document.getElementById("agentfeed");
  showFeed() {
    this.feed?.classList.add("show");
  }
  /** Append one activity row (newest at top). Caps the list so it can't grow
   *  unbounded over a long watch. `html` is trusted, pre-escaped by the caller. */
  pushFeed(html: string) {
    if (!this.feed) return;
    const row = document.createElement("div");
    row.innerHTML = html;
    // insert just after the header so newest sits on top
    const head = this.feed.querySelector(".feedhead");
    if (head && head.nextSibling) this.feed.insertBefore(row, head.nextSibling);
    else this.feed.appendChild(row);
    while (this.feed.querySelectorAll(".feedrow").length > 24) {
      const last = this.feed.querySelector(".feedrow:last-child");
      if (last) last.remove(); else break;
    }
  }
  clearFeed() {
    if (!this.feed) return;
    this.feed.querySelectorAll(".feedrow").forEach((r) => r.remove());
  }

  // ── Toast ──
  toastMsg(text: string, ms = 2600) {
    this.toast.textContent = text;
    this.toast.classList.add("show");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(
      () => this.toast.classList.remove("show"),
      ms,
    );
  }

  // ── Wristwatch ──
  setWatchUp(up: boolean) {
    this.watch.classList.toggle("up", up);
  }
  setWatchTime(time: string, label: string) {
    this.watchDigits.textContent = time;
    this.watchLabel.textContent = label;
  }

  // ── Dread / damage ──
  setDread(level: number) {
    const l = Math.max(0, Math.min(1, level));
    const blur = 40 + l * 90;
    const spread = 20 + l * 80;
    const alpha = 0.06 + l * 0.78;
    this.vignette.style.boxShadow = `inset 0 0 ${blur}px ${spread}px rgba(0,0,0,${alpha})`;
  }
  redFlash() {
    this.redflash.style.opacity = "0.85";
    setTimeout(() => (this.redflash.style.opacity = "0"), 120);
  }

  // ── Dialogue ──
  showDialogue(say: string, choices: DialogueChoice[], onChoose: (id: string) => void) {
    this.dialogueSay.textContent = say;
    this.renderChoices(choices, onChoose);
    this.dialogue.classList.add("show");
  }
  /** Update just the spoken line + choices on an already-open dialogue. */
  updateDialogue(say: string, choices: DialogueChoice[], onChoose: (id: string) => void) {
    this.dialogueSay.textContent = say;
    this.renderChoices(choices, onChoose);
  }
  hideDialogue() {
    this.dialogue.classList.remove("show");
    this.dialogueChoices.innerHTML = "";
  }
  private renderChoices(choices: DialogueChoice[], onChoose: (id: string) => void) {
    this.dialogueChoices.innerHTML = "";
    choices.forEach((c, i) => {
      const b = document.createElement("button");
      b.innerHTML = `<span class="num">${i + 1}</span>${c.label}`;
      b.onclick = () => onChoose(c.id);
      this.dialogueChoices.appendChild(b);
    });
  }
  /** Are we mid-conversation (choices on screen)? Used to route number keys. */
  get dialogueOpen() {
    return this.dialogue.classList.contains("show");
  }
  chooseByIndex(i: number) {
    const buttons = this.dialogueChoices.querySelectorAll("button");
    const b = buttons[i] as HTMLButtonElement | undefined;
    if (b) b.click();
  }

  // ── Screens ──
  hideScreen(id: string) {
    el(id).classList.add("hidden");
  }
  showScreen(id: string) {
    el(id).classList.remove("hidden");
  }
  showDead(reason: string, flatline: string) {
    el("dead-reason").textContent = reason;
    el("flatline").textContent = flatline;
    this.showScreen("screen-dead");
  }
  showWin(stats: string) {
    el("win-stats").textContent = stats;
    this.showScreen("screen-win");
  }
  onClick(id: string, cb: () => void) {
    el(id).addEventListener("click", cb);
  }
}
